import React from "react";
import styled from "styled-components";
import { Alert, Card, Col, Empty, Row, Skeleton, Space, Tag, Typography } from "antd";
import LiveTripBoardWidget from "./LiveTripBoardWidget.jsx";
import RouteVolumeWidget from "./RouteVolumeWidget.jsx";
import VehicleUtilizationWidget from "./VehicleUtilizationWidget.jsx";
import DelayIncidentAnalysisWidget from "./DelayIncidentAnalysisWidget.jsx";

const { Text, Title } = Typography;

const ShellSection = styled.div`
  margin-bottom: 24px;
`;

const SlotCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const OperationsDashboardShell = ({
  metricGroups,
  dashboardFilters,
  loading = false,
  error = null,
  canView = false,
}) => {
  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Operations dashboard access required"
        description="You need dashboard, order, or operations reporting permissions to view this dashboard."
      />
    );
  }

  if (loading) {
    return (
      <ShellSection data-testid="operations-dashboard-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </ShellSection>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Operations dashboard could not load"
        description={error}
      />
    );
  }

  const operationsGroup = metricGroups?.operations;
  const operationsMetrics = operationsGroup?.metrics || [];

  return (
    <ShellSection data-testid="operations-dashboard-shell">
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <SlotCard>
          <Space direction="vertical" size={8}>
            <Title level={4}>Operations Dashboard</Title>
            <Text type="secondary">
              Trips, bookings, route volume, vehicle utilization, delays, exceptions,
              and live operational status using the shared dashboard filters.
            </Text>
            <FilterSummary dashboardFilters={dashboardFilters} />
          </Space>
        </SlotCard>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <OperationsMetricCard
              title="Active Orders"
              metrics={operationsMetrics}
              metricKeys={["active_orders"]}
            />
          </Col>
          <Col xs={24} md={8}>
            <OperationsMetricCard
              title="Scheduled Bookings"
              metrics={operationsMetrics}
              metricKeys={["scheduled_bookings"]}
            />
          </Col>
          <Col xs={24} md={8}>
            <OperationsMetricCard
              title="Active Trips"
              metrics={operationsMetrics}
              metricKeys={["active_trips"]}
            />
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <LiveTripBoardWidget
              board={operationsGroup?.metadata?.liveTripBoard}
            />
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <RouteVolumeWidget
              routeVolume={operationsGroup?.metadata?.routeVolume}
            />
          </Col>
          <Col xs={24} lg={12}>
            <VehicleUtilizationWidget
              vehicleUtilization={operationsGroup?.metadata?.vehicleUtilization}
            />
          </Col>
          <Col xs={24} lg={12}>
            <DelayIncidentAnalysisWidget
              analysis={operationsGroup?.metadata?.delayIncidentAnalysis}
            />
          </Col>
        </Row>
      </Space>
    </ShellSection>
  );
};

const FilterSummary = ({ dashboardFilters }) => {
  const time = dashboardFilters?.time || {};
  const activeFilters = [
    ["Date", time.datePreset || "last_7_days"],
    ["Route", dashboardFilters?.operational?.route],
    ["Province", dashboardFilters?.operational?.province],
    ["Vehicle", dashboardFilters?.operational?.vehicleId],
    ["Driver", dashboardFilters?.operational?.driverId],
    ["Team", dashboardFilters?.operational?.teamId],
    ["Booking", dashboardFilters?.operational?.bookingStatus],
    ["Ops", dashboardFilters?.operational?.operationalStatus],
  ].filter(([, value]) => value && value !== "all");

  return (
    <Space size={[8, 8]} wrap>
      {activeFilters.map(([label, value]) => (
        <Tag key={label} color="blue">
          {label}: {value}
        </Tag>
      ))}
    </Space>
  );
};

const OperationsMetricCard = ({ title, metrics, metricKeys }) => {
  const relevantMetrics = metrics.filter((metric) => metricKeys.includes(metric.key));

  return (
    <SlotCard title={title}>
      {relevantMetrics.length > 0 ? (
        <Space size={[8, 8]} wrap>
          {relevantMetrics.map((metric) => (
            <Tag key={metric.key} color="green">
              {metric.label}: {formatMetricValue(metric)}
            </Tag>
          ))}
        </Space>
      ) : (
        <Empty
          description="Waiting for operations metric payload"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </SlotCard>
  );
};

const OperationsSlotCard = ({ title, description }) => (
  <SlotCard title={title}>
    <Space direction="vertical" size={12}>
      <Text type="secondary">{description}</Text>
      <Empty
        description="Widget slot ready"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    </Space>
  </SlotCard>
);

function formatMetricValue(metric) {
  return new Intl.NumberFormat("en-ZA").format(Number(metric.value || 0));
}

export default OperationsDashboardShell;
